import type { UserDto } from "@reactive-resume/dto";
import { useQuery } from "@tanstack/react-query";
import type { AxiosResponse } from "axios";
import { useEffect } from "react";

import {
  countries,
  countryNotProvided,
  findCurrency,
} from "@/client/constants/country-preferences";
import { axios } from "@/client/libs/axios";
import { useAuthStore } from "@/client/stores/auth";
import {
  type CountryPreference,
  useCountryPreferenceStore,
} from "@/client/stores/country-preference";

export const fetchUser = async () => {
  const response = await axios.get<UserDto | undefined, AxiosResponse<UserDto | undefined>>(
    "/user/me",
  );

  return response.data ?? null;
};

export const useUser = () => {
  const setUser = useAuthStore((state) => state.setUser);

  const setPreference = useCountryPreferenceStore((state) => state.setPreference);

  const clearPreference = useCountryPreferenceStore((state) => state.clearPreference);

  const {
    error,
    isPending: loading,
    data: user,
  } = useQuery({
    queryKey: ["user"],
    queryFn: fetchUser,
  });

  useEffect(() => {
    setUser(user ?? null);

    /*
     * On ne touche pas au cache pendant le chargement
     * ni lorsqu’une erreur réseau temporaire survient.
     */
    if (loading || error) {
      return;
    }

    if (!user) {
      clearPreference();
      return;
    }

    if (!user.countryPreferenceSetAt || !user.pricingCurrencyCode) {
      clearPreference();
      return;
    }

    const currency = findCurrency(user.pricingCurrencyCode);

    if (!currency) {
      return;
    }

    const basePreference = user.countryNotProvided
      ? countryNotProvided
      : countries.find((country) => country.countryCode === user.countryCode);

    if (!basePreference) {
      return;
    }

    const preference: CountryPreference = {
      ...basePreference,
      countryCode: user.countryCode ?? null,
      currencyCode: user.pricingCurrencyCode,
      currencyLabel: currency.label,
      countryNotProvided: user.countryNotProvided,
    };

    setPreference(preference);
  }, [clearPreference, error, loading, setPreference, setUser, user]);

  return {
    user,
    loading,
    error,
  };
};

export const useIsNewUser = (enabled = true) => {
  const {
    data,
    error,
    isPending: loading,
  } = useQuery({
    queryKey: ["user", "is-new-user"],
    queryFn: async () => {
      const response = await axios.get<{
        isNewUser: boolean;
      }>("/ai/is-new-user");

      return response.data.isNewUser;
    },
    enabled,
    staleTime: 60_000,
  });

  return {
    isNewUser: data === true,
    loading,
    error,
  };
};
